export const roles = [
  "hospital_owner",
  "admin",
  "doctor",
  "nurse",
  "reception",
  "billing",
  "radiology",
  "therapist",
  "read_only",
] as const;

export type Role = (typeof roles)[number];

export const databaseEnvironments = ["preview", "production"] as const;
export type DatabaseEnvironment = (typeof databaseEnvironments)[number];

export const dataClassifications = [
  "synthetic_only",
  "regulated_clinical",
] as const;
export type DataClassification = (typeof dataClassifications)[number];

export function isDatabaseEnvironment(value: unknown): value is DatabaseEnvironment {
  return (
    typeof value === "string" &&
    databaseEnvironments.some((environment) => environment === value)
  );
}

export function isDataClassification(value: unknown): value is DataClassification {
  return (
    typeof value === "string" &&
    dataClassifications.some((classification) => classification === value)
  );
}

export const permissions = [
  "hospital:manage",
  "user:manage",
  "patient:read",
  "patient:write",
  "reception:read",
  "reception:create",
  "reception:manage",
  "encounter:read",
  "encounter:start",
  "encounter:complete",
  "clinical:read",
  "clinical:write",
  "order:read",
  "order:draft",
  "order:confirm",
  "worklist:read",
  "assistant:query",
  "reminder:read",
  "reminder:write",
  "audit:read",
] as const;

export type Permission = (typeof permissions)[number];

const permissionMatrix: Readonly<Record<Role, ReadonlySet<Permission>>> = {
  hospital_owner: new Set(permissions),
  admin: new Set(permissions),
  doctor: new Set([
    "patient:read",
    "patient:write",
    "reception:read",
    "reception:create",
    "reception:manage",
    "encounter:read",
    "encounter:start",
    "encounter:complete",
    "clinical:read",
    "clinical:write",
    "order:read",
    "order:draft",
    "order:confirm",
    "worklist:read",
    "assistant:query",
    "reminder:read",
    "reminder:write",
    "audit:read",
  ]),
  nurse: new Set([
    "patient:read",
    "patient:write",
    "reception:read",
    "reception:create",
    "reception:manage",
    "encounter:read",
    "encounter:start",
    "clinical:read",
    "order:read",
    "reminder:read",
    "audit:read",
  ]),
  reception: new Set([
    "patient:read",
    "patient:write",
    "reception:read",
    "reception:create",
    "reception:manage",
    "encounter:read",
  ]),
  billing: new Set([
    "patient:read",
    "reception:read",
    "encounter:read",
    "audit:read",
  ]),
  radiology: new Set([
    "patient:read",
    "reception:read",
    "encounter:read",
    "clinical:read",
    "order:read",
    "worklist:read",
  ]),
  therapist: new Set([
    "patient:read",
    "reception:read",
    "encounter:read",
    "clinical:read",
    "order:read",
  ]),
  read_only: new Set([
    "patient:read",
    "reception:read",
    "encounter:read",
    "clinical:read",
    "order:read",
  ]),
};

export function can(role: Role, permission: Permission): boolean {
  return permissionMatrix[role].has(permission);
}

export function isRole(value: string): value is Role {
  return roles.some((role) => role === value);
}

export type ReceptionStatus = "registered" | "waiting" | "called" | "cancelled";
export type EncounterStatus = "planned" | "in_progress" | "completed" | "cancelled";

export function isReceptionStatus(value: unknown): value is ReceptionStatus {
  return (
    value === "registered" ||
    value === "waiting" ||
    value === "called" ||
    value === "cancelled"
  );
}

export function isEncounterStatus(value: unknown): value is EncounterStatus {
  return (
    value === "planned" ||
    value === "in_progress" ||
    value === "completed" ||
    value === "cancelled"
  );
}

const receptionTransitions: Readonly<Record<ReceptionStatus, readonly ReceptionStatus[]>> = {
  registered: ["waiting", "cancelled"],
  waiting: ["called", "cancelled"],
  called: ["cancelled"],
  cancelled: [],
};

const encounterTransitions: Readonly<Record<EncounterStatus, readonly EncounterStatus[]>> = {
  planned: ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: [],
};

export class DomainRuleError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "DomainRuleError";
    this.code = code;
  }
}

export function assertReceptionTransition(
  current: ReceptionStatus,
  next: ReceptionStatus,
): void {
  if (!receptionTransitions[current].includes(next)) {
    throw new DomainRuleError(
      "INVALID_RECEPTION_TRANSITION",
      `접수 상태를 ${current}에서 ${next}(으)로 변경할 수 없습니다.`,
    );
  }
}

export function assertEncounterTransition(
  current: EncounterStatus,
  next: EncounterStatus,
): void {
  if (!encounterTransitions[current].includes(next)) {
    throw new DomainRuleError(
      "INVALID_ENCOUNTER_TRANSITION",
      `진료 상태를 ${current}에서 ${next}(으)로 변경할 수 없습니다.`,
    );
  }
}

export function normalizeChartNumber(value: string): string {
  const normalized = value.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
  if (normalized.length < 4 || normalized.length > 32) {
    throw new DomainRuleError(
      "INVALID_CHART_NUMBER",
      "차트번호는 영문·숫자·하이픈 4~32자로 입력하십시오.",
    );
  }
  return normalized;
}

export function requireNonBlank(value: unknown, fieldName: string, maxLength: number): string {
  if (typeof value !== "string") {
    throw new DomainRuleError("INVALID_INPUT", `${fieldName}을(를) 입력하십시오.`);
  }
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength) {
    throw new DomainRuleError(
      "INVALID_INPUT",
      `${fieldName}은(는) 1~${maxLength}자로 입력하십시오.`,
    );
  }
  return normalized;
}

export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().startsWith(value);
}

export function assertPermission(role: Role, permission: Permission): void {
  if (!can(role, permission)) {
    throw new DomainRuleError(
      "FORBIDDEN",
      `${role} 역할에는 ${permission} 권한이 없습니다.`,
    );
  }
}
